/**
 * ChatView - a single conversation.
 *
 * Features:
 *  - scripted author replies with a ragged typing indicator;
 *  - sent (✓) / read (✓✓) checkmarks on own messages;
 *  - inline photo/video media (author templates + user uploads) opening in a
 *    pinch-zoom viewer;
 *  - attach menu to send photos/videos from gallery or camera;
 *  - long-press own text messages to edit them;
 *  - a phone button (shown once the author offers a call) that starts the mock
 *    video call overlay;
 *  - an empty-chat placeholder prompting the user to say hi.
 *
 * State that must persist lives in the chat store (via the callbacks); purely
 * transient UI state (attach menu, editing target, open viewer/call) is local.
 */
import { useRef, useEffect, useState, useCallback } from 'preact/hooks';
import { AuthorAvatar } from './AuthorAvatar.jsx';
import { MediaThumb } from './MediaThumb.jsx';
import { MediaViewer } from './MediaViewer.jsx';
import { CallOverlay } from './CallOverlay.jsx';
import { CameraCapture } from './CameraCapture.jsx';
import {
  IconChevronLeft,
  IconPhone,
  IconPlus,
  IconSend,
  IconImage,
  IconCamera,
  IconCheck,
  IconCheckCheck,
  IconX,
} from './icons.jsx';
import { putBlob } from '../lib/mediaStore.js';

const LONG_PRESS_MS = 450;

export function ChatView({ chat, onBack, onSend, onSendMedia, onEdit, onEndCall }) {
  const inputRef = useRef(null);
  const endRef = useRef(null);
  const galleryRef = useRef(null);
  const pressTimer = useRef(0);

  const [attachOpen, setAttachOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [attachment, setAttachment] = useState(null); // { kind, blobId, url } staged on the input
  const [editing, setEditing] = useState(null); // { id, text }
  const [menuFor, setMenuFor] = useState(null); // message id for context menu
  const [viewer, setViewer] = useState(null); // { kind, url }
  const [inCall, setInCall] = useState(false);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [chat?.messages.length, chat?.pending]);

  // Keep the compose box in sync when entering/leaving edit mode.
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.value = editing.text;
      inputRef.current.focus();
    }
  }, [editing]);

  // Revoke the staged attachment's preview URL when it changes or on unmount.
  useEffect(() => {
    const url = attachment?.url;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [attachment]);

  const submit = useCallback(
    (e) => {
      e.preventDefault();
      const text = inputRef.current.value;
      if (editing) {
        if (!text.trim()) return;
        onEdit?.(chat.id, editing.id, text);
        setEditing(null);
        inputRef.current.value = '';
        return;
      }
      // A staged attachment is sent together with the (optional) text.
      if (attachment) {
        onSendMedia?.(chat.id, { kind: attachment.kind, blobId: attachment.blobId }, text);
        setAttachment(null);
        inputRef.current.value = '';
        return;
      }
      if (!text.trim()) return;
      onSend(chat.id, text);
      inputRef.current.value = '';
    },
    [chat, editing, attachment, onSend, onSendMedia, onEdit],
  );

  // Stage a file on the input instead of sending immediately.
  const stageFile = useCallback(async (file) => {
    if (!file) return;
    const kind = file.type.startsWith('video') ? 'video' : 'photo';
    const blobId = await putBlob(file);
    const url = URL.createObjectURL(file);
    setAttachment({ kind, blobId, url });
    inputRef.current?.focus();
  }, []);

  const onFile = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      setAttachOpen(false);
      stageFile(file);
    },
    [stageFile],
  );

  const onCameraCapture = useCallback(
    ({ file }) => {
      setCameraOpen(false);
      stageFile(file);
    },
    [stageFile],
  );

  const startPress = useCallback((m) => {
    if (m.from !== 'me' || m.kind === 'call' || !m.text) return;
    pressTimer.current = window.setTimeout(() => setMenuFor(m.id), LONG_PRESS_MS);
  }, []);
  const cancelPress = useCallback(() => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = 0;
    }
  }, []);

  if (!chat) return null;

  const isEmpty = chat.messages.length === 0 && !chat.pending;
  const isTyping = !!chat.pending?.typing;
  const headerStatus = isTyping ? 'typing…' : 'online';

  return (
    <div class="chat">
      <header class="chat__header">
        <button type="button" class="chat__back" onClick={onBack} aria-label="Back">
          <IconChevronLeft size={26} />
        </button>
        <AuthorAvatar letter={chat.letter} seed={chat.id} size={36} />
        <div class="chat__title">
          <span class="chat__name">{chat.authorName}</span>
          <span class={`chat__status ${isTyping ? 'is-typing' : ''}`}>{headerStatus}</span>
        </div>
        <button
          type="button"
          class={`chat__call ${chat.callOffered ? 'is-offered' : ''}`}
          onClick={() => setInCall(true)}
          aria-label="Call author"
        >
          <IconPhone size={20} />
        </button>
      </header>

      <div class="chat__messages" onClick={() => menuFor && setMenuFor(null)}>
        {isEmpty && (
          <div class="chat__placeholder">
            <p class="chat__placeholder-title">Say hi to {chat.authorName} 👋</p>
            <p class="chat__placeholder-sub">
              Send a message to start the conversation. Be sweet 😉
            </p>
          </div>
        )}

        {chat.messages.map((m) =>
          m.kind === 'call' ? (
            <div key={m.id} class="chat__callrow">
              <IconPhone size={14} /> {m.text}
            </div>
          ) : (
            <div
              key={m.id}
              class={`chat__bubble chat__bubble--${m.from}`}
              onPointerDown={() => startPress(m)}
              onPointerUp={cancelPress}
              onPointerLeave={cancelPress}
            >
              {m.media && <MediaThumb media={m.media} onOpen={setViewer} />}
              {m.text && <span class="chat__text">{m.text}</span>}
              <span class="chat__meta">
                {m.edited && <span class="chat__edited">edited</span>}
                {m.from === 'me' && (
                  <span class={`chat__ticks ${m.status === 'read' ? 'is-read' : ''}`}>
                    {m.status === 'read' ? <IconCheckCheck size={15} /> : <IconCheck size={15} />}
                  </span>
                )}
              </span>

              {menuFor === m.id && (
                <div class="chat__ctxmenu">
                  <button
                    type="button"
                    onClick={() => {
                      setEditing({ id: m.id, text: m.text });
                      setMenuFor(null);
                    }}
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
          ),
        )}

        {isTyping && (
          <div class="chat__bubble chat__bubble--author chat__bubble--typing">
            <span class="chat__dot" />
            <span class="chat__dot" />
            <span class="chat__dot" />
          </div>
        )}
        <div ref={endRef} />
      </div>

      {editing && (
        <div class="chat__editbar">
          Editing message
          <button type="button" onClick={() => { setEditing(null); inputRef.current.value = ''; }}>
            <IconX size={16} />
          </button>
        </div>
      )}

      {attachment && (
        <div class="chat__attachpreview">
          {attachment.kind === 'video' ? (
            <video class="chat__attachpreview-media" src={attachment.url} muted playsinline />
          ) : (
            <img class="chat__attachpreview-media" src={attachment.url} alt="attachment" />
          )}
          <button
            type="button"
            class="chat__attachpreview-remove"
            onClick={() => setAttachment(null)}
            aria-label="Remove attachment"
          >
            <IconX size={14} />
          </button>
        </div>
      )}

      <form class="chat__compose" onSubmit={submit}>
        <button
          type="button"
          class="chat__attach"
          onClick={() => setAttachOpen((v) => !v)}
          aria-label="Attach media"
        >
          <IconPlus size={22} />
        </button>
        {attachOpen && (
          <div class="chat__attachmenu">
            <button type="button" onClick={() => galleryRef.current?.click()}>
              <IconImage size={18} /> Gallery
            </button>
            <button
              type="button"
              onClick={() => {
                setAttachOpen(false);
                setCameraOpen(true);
              }}
            >
              <IconCamera size={18} /> Camera
            </button>
          </div>
        )}
        <input
          ref={inputRef}
          class="chat__input"
          type="text"
          placeholder={editing ? 'Edit message…' : 'Message…'}
          autocomplete="off"
        />
        <button type="submit" class="chat__send" aria-label="Send">
          <IconSend size={20} />
        </button>

        <input
          ref={galleryRef}
          type="file"
          accept="image/*,video/*"
          hidden
          onChange={onFile}
        />
      </form>

      {cameraOpen && (
        <CameraCapture onCapture={onCameraCapture} onClose={() => setCameraOpen(false)} />
      )}
      {viewer && <MediaViewer media={viewer} onClose={() => setViewer(null)} />}
      {inCall && (
        <CallOverlay
          chat={chat}
          onEnd={(dur) => {
            setInCall(false);
            onEndCall?.(chat.id, dur);
          }}
        />
      )}
    </div>
  );
}
